// ---------------------------------------------------------------------------
// Garder le contenu pré-généré à l'écran jusqu'à ce que l'application prenne
// le relais.
//
// Une page SSG arrive avec son contenu DANS `#orbit_front_app`. Or React vide
// son conteneur au premier commit : le contenu disparaissait donc le temps que
// l'application se monte et rende ses blocs. Avec l'amorçage des réponses
// (`primeRequestCache`) ce trou est passé de ~1 s à ~150 ms, mais il reste
// visible — un clignotement sur la moitié haute de la page.
//
// La parade ne touche ni React ni les composants : on SORT le contenu
// pré-généré du conteneur avant le montage, on le laisse peint à sa place, et
// on l'efface quand l'application a rendu l'équivalent. React démarre donc dans
// un conteneur vide, comme sur une page ordinaire — il n'a rien à effacer.
//
// Le contenu sorti n'est PAS interactif (aucun gestionnaire React n'y est
// attaché) : seuls les liens y fonctionnent. C'est acceptable pour la fraction
// de seconde qu'il vit, et c'est déjà ce qu'était le contenu pré-généré avant
// le montage.
// ---------------------------------------------------------------------------

/** Part au bout de ce délai même si l'application n'a rien rendu. */
const ABANDON_MS = 5000;

/** Ce que l'application doit avoir rendu, en part du texte pré-généré. */
const SEUIL = 0.5;

/**
 * @param {HTMLElement} container le conteneur de l'application
 * @returns {boolean} vrai si un contenu pré-généré a été mis de côté
 */
export function holdSsgPaint(container) {
  if (!container?.firstChild) return false;

  const ghost = document.createElement('div');
  ghost.id = 'orbit_ssg_paint';
  while (container.firstChild) ghost.appendChild(container.firstChild);
  container.parentNode.insertBefore(ghost, container);

  const attendu = (ghost.textContent || '').trim().length * SEUIL;
  // Le conteneur reste dans le flux mais invisible : l'écran de chargement de
  // `BuilderPublicRenderer` ne doit pas s'afficher SOUS le contenu déjà peint.
  const displayInitial = container.style.display;
  container.style.display = 'none';

  let fini = false;
  const rendreLaMain = () => {
    if (fini) return;
    fini = true;
    observer.disconnect();
    clearTimeout(minuteur);
    container.style.display = displayInitial;
    ghost.remove();
  };

  const observer = new MutationObserver(() => {
    if ((container.textContent || '').trim().length >= attendu) rendreLaMain();
  });
  observer.observe(container, { childList: true, subtree: true, characterData: true });

  // Au bout du délai, on ne rend la main QUE si l'application a rendu quelque
  // chose. Sinon — erreur JS, ressource manquante, appareil très lent — retirer
  // le contenu pré-généré remplacerait une page parfaitement lisible par une
  // page blanche : exactement l'inverse de ce que le statique sert à garantir.
  // L'observateur reste armé ; l'application peut encore arriver.
  const minuteur = setTimeout(() => {
    if ((container.textContent || '').trim().length > 0) rendreLaMain();
  }, ABANDON_MS);

  return true;
}
